import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, input } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap/modal';

import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { NgAceSubmenu } from '@app/_ace/directives/ace-submenu.directive';
import { AcceptTermsDialogComponent } from '@app/_dialogs/accept-terms-dialog/accept-terms-dialog.component';
import { IframeDialogComponent } from '@app/_dialogs/iframe-dialog/iframe-dialog.component';
import { PdfViewerDialogComponent } from '@app/_dialogs/pdf-viewer-dialog/pdf-viewer-dialog.component';
import { AccountService } from '@app/_services/account.service';
import { ErrorHandlerService } from '@app/_services/errorHandler.service';
import { InvoiceService } from '@app/_services/invoice.service';
import { CollapseDirective } from 'ngx-bootstrap/collapse';
import { TooltipDirective } from 'ngx-bootstrap/tooltip';
import { NgAceSubmenu as NgAceSubmenu_1 } from '../../_ace/directives/ace-submenu.directive';

@Component({
  selector: 'app-sidebar-submenu',
  templateUrl: './submenu.component.html',
  styleUrls: ['./submenu.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgTemplateOutlet,
    NgClass,
    RouterLinkActive,
    RouterLink,
    TooltipDirective,
    CollapseDirective,
    NgAceSubmenu_1,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class SidebarSubmenuComponent {
  private readonly parentSubmenu = inject(NgAceSubmenu);
  private readonly invoiceService = inject(InvoiceService);
  private readonly errorHandler = inject(ErrorHandlerService);
  private readonly translate = inject(TranslateService);
  private readonly modalService = inject(BsModalService);
  private readonly destroyRef = inject(DestroyRef);

  readonly accountService = inject(AccountService);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly items = input.required<Record<string, any>[]>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  toggleSubmenu(navItem: Record<string, any>, navItemSub?: NgAceSubmenu) {
    // Only toggle when sidemenu is active, not in horizontal menu.
    if (globalThis.outerWidth < 1200) {
      navItem.isToggling = true; // adds '.is-toggling' class which is used in CSS themes

      // `parentSubmenu` is usually the main `.nav` in `.sidebar` (the parent `NgAceSubmenu`)
      this.parentSubmenu.toggle(navItem, navItemSub);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getRouterLink(navItem: Record<string, any>): string[] | undefined {
    if (navItem.link) {
      return ['/' + navItem.link];
    } else {
      return undefined;
    }
  }

  collapseSubmenu(navItemOpen: boolean) {
    // Only collapse submenus when sidemenu is active, not in horizontal menu.
    if (globalThis.outerWidth >= 1200) {
      return false;
    } else {
      return !navItemOpen;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onLinkClick(navItem: Record<string, any>) {
    if (navItem.action) {
      if (navItem.action == 'acceptInvoiceTerms') {
        this.showAcceptInvoiceTermsDialog();
      } else if (navItem.action == 'openInvoiceManual') {
        this.openInvoiceManual();
      } else if (navItem.action == 'openInvoiceTerms') {
        this.openInvoiceTerms();
      }
    }
  }

  showAcceptInvoiceTermsDialog(): void {
    AcceptTermsDialogComponent.showDialogTranslated(
      this.translate,
      this.modalService,
      '/html/Nutzungsbedingungen_Modul_Invoice.html',
      'Invoice.AcceptTermsDialog.Title',
      'Invoice.AcceptTermsDialog.Text',
      'Global.Ok',
      'Global.Cancel',
      'modal-1000',
    ).then((modalRef) => {
      modalRef.content?.onClose.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((onCloseResult) => {
        if (onCloseResult.accepted) {
          this.acceptInvoiceTerms();
        }
      });
    });
  }

  acceptInvoiceTerms(): void {
    this.invoiceService
      .acceptTerms()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.accountService.updateUserRoles();
        },
        error: (errorResponse: HttpErrorResponse) => {
          this.errorHandler.displayErrorDialog(errorResponse);
        },
      });
  }

  openInvoiceManual(): void {
    this.translate
      .get(['Nav.Links.InvoiceManual', 'Url.InvoiceManualPdf'])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((translations) => {
        PdfViewerDialogComponent.showDialog(
          this.modalService,
          translations['Nav.Links.InvoiceManual'],
          translations['Url.InvoiceManualPdf'],
        );
      });
  }

  openInvoiceTerms(): void {
    IframeDialogComponent.showDialogTranslated(
      this.translate,
      this.modalService,
      '/html/Nutzungsbedingungen_Modul_Invoice.html',
      'Nav.Links.InvoiceTerms',
      undefined,
      'Global.Close',
      undefined,
      'modal-1000',
    );
  }
}
